import { Box } from '@mui/material'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useAppSelector } from '../../../app/hooks'
import { useGetCountriesQuery } from '../../../app/redux-fetch/apiCoreQuery'
import {
  useGetAllInventoryUnitsQuery,
  useGetProductCategoriesQuery,
} from '../../../app/redux-fetch/apiProducts'
import { useGetCustomersQuery } from '../../../app/redux-fetch/apiQuery'
import { IDropDownItem } from '../../../app/types'
import FormInput from '../../../components/Forms/FormInput'
import FormSelect, { Option } from '../../../components/Forms/FormSelect'
import { SectionTitleTypography } from '../../../components/Typographies/styles'
import { fetchLoggedInUserSelector } from '../../selectors'
import { ILoggedInUser } from '../../types'
import messages from './messages'

type MainInfoProps = {
  isDisabled?: boolean
  customerId?: number
}

const MainInfo = ({ isDisabled = false, customerId: sharedCustomerId = -1 }: MainInfoProps) => {
  const { formatMessage } = useIntl()
  const loggedInUser = useAppSelector(fetchLoggedInUserSelector.data) || ({} as ILoggedInUser)
  const { getValues } = useFormContext()

  const isCustomerUser = sharedCustomerId > 0

  const { data: countries = [] } = useGetCountriesQuery(undefined, { skip: isDisabled })
  const { data: inventoryUnits = [] } = useGetAllInventoryUnitsQuery(undefined, { skip: isDisabled })
  const { data: customers } = useGetCustomersQuery(loggedInUser.tenantId ?? -1, { skip: isCustomerUser })
  const { data: productCategories = [] } = useGetProductCategoriesQuery(
    {
      tenantId: loggedInUser.tenantId ?? -1,
      customerId: sharedCustomerId,
    },
    { skip: isDisabled },
  )

  const mapToOptions = (items: any[] = []): Option[] => {
    if (!Array.isArray(items)) return []
    return items.map((item) => ({
      label: item.name || '',
      value: item.id || 0,
    }))
  }

  const singleOption = (id: unknown, name: unknown): Option[] => {
    if (id === undefined || id === null || id === 0) return []
    return [{ label: (name as string) || '', value: id as string | number }]
  }

  const productCategoryOptions = isDisabled
    ? singleOption(getValues('productCategoryId'), getValues('productCategory.name'))
    : mapToOptions(productCategories as IDropDownItem[])

  const countryOptions = isDisabled
    ? singleOption(getValues('countryOfOriginId'), getValues('countryOfOrigin.name'))
    : mapToOptions(countries as any)

  const inventoryUnitOptions = isDisabled
    ? singleOption(getValues('inventoryUnitId'), getValues('inventoryUnit.name'))
    : mapToOptions(inventoryUnits as any)

  const customerOptions = mapToOptions((customers as IDropDownItem[]) || [])

  return (
    <Box sx={{ width: '50%', mt: '16px' }}>
      <Box sx={{ mb: '16px' }}>
        <SectionTitleTypography>{formatMessage(messages.mainInfoLbl)}</SectionTitleTypography>
      </Box>
      {!isCustomerUser && (
        <Box sx={{ mb: '8px' }}>
          <FormSelect
            name='customerId'
            label={formatMessage(messages.customerLbl)}
            options={customerOptions}
            disabled
            required
          />
        </Box>
      )}
      <Box sx={{ mb: '8px' }}>
        <FormInput
          name='sku'
          label={formatMessage(messages.productCode)}
          disabled={isDisabled}
          required
          maxLength={50}
          inputPattern={/[^a-zA-Z0-9\-_.#,]/g}
        />
      </Box>
      <Box sx={{ mb: '8px' }}>
        <FormInput
          name='name'
          label={formatMessage(messages.productName)}
          disabled={isDisabled}
          required
          maxLength={250}
        />
      </Box>
      <Box sx={{ mb: '8px' }}>
        <FormSelect
          name='productCategoryId'
          label={formatMessage(messages.productCategory)}
          options={productCategoryOptions}
          disabled={isDisabled}
          required
        />
      </Box>
      <Box sx={{ mb: '8px' }}>
        <FormSelect
          name='countryOfOriginId'
          label={formatMessage(messages.countryOfOrigin)}
          options={countryOptions}
          disabled={isDisabled}
          required
        />
      </Box>
      <Box sx={{ mb: '8px' }}>
        <FormSelect
          name='inventoryUnitId'
          label={formatMessage(messages.inventoryUnit)}
          options={inventoryUnitOptions}
          disabled={isDisabled}
          required
        />
      </Box>
      <Box sx={{ mb: '8px' }}>
        <FormInput
          name='barcode'
          label={formatMessage(messages.barcode)}
          disabled={isDisabled}
          maxLength={50}
          inputPattern={/[^a-zA-Z0-9]/g}
        />
      </Box>
    </Box>
  )
}

export default MainInfo
